import {
  Alert,
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { usePatient, useUpdatePatient, type UpdatePatientInput } from '../api/patients';
import { parseMutationError } from '../api/errors';
import { useHasPermission, useMe } from '../api/session';
import { ReasonConfirmModal } from './reason-confirm-modal';

interface Props {
  patientId: string;
}

interface DraftFields {
  givenName: string;
  familyName: string;
  dob: string;
  sexAtBirth: string;
  postalCode: string;
  email: string;
  phone: string;
  genderIdentity: string;
}

/**
 * Patient-attributes panel (case-triage capability 1b). Renders — and
 * fetches patient PHI — only when the operator holds case_triage:write.
 * Editing is further restricted to super_admins: other operators see the
 * fields read-only with no Save action.
 */
export function PatientAttributesCard({ patientId }: Props) {
  const canView = useHasPermission('case_triage:write');
  const me = useMe();
  const canEdit = me.data?.role === 'super_admin';
  const query = usePatient(canView ? patientId : undefined);
  const updatePatient = useUpdatePatient(patientId);

  const [draft, setDraft] = useState<DraftFields | null>(null);
  const [dirty, setDirty] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Partial<UpdatePatientInput> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (dirty) return;
    if (query.data) {
      setDraft({
        givenName: query.data.given_name,
        familyName: query.data.family_name,
        dob: query.data.dob,
        sexAtBirth: query.data.sex_at_birth ?? '',
        postalCode: query.data.postal_code ?? '',
        email: query.data.email ?? '',
        phone: query.data.phone ?? '',
        genderIdentity: query.data.gender_identity ?? '',
      });
    }
    // Only re-derive the draft when the fetched patient identity/data
    // itself changes — this intentionally excludes local draft state.
    // Skipped entirely while `dirty` is true so an in-progress edit is
    // never clobbered by a background refetch (e.g. window refocus).
  }, [query.data, dirty]);

  if (!canView) return null;

  function setField<K extends keyof DraftFields>(key: K, value: string) {
    setDirty(true);
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  function handleSave() {
    if (!draft || !query.data) return;
    const changes: Partial<UpdatePatientInput> = {};
    if (draft.givenName !== query.data.given_name) changes.givenName = draft.givenName;
    if (draft.familyName !== query.data.family_name) changes.familyName = draft.familyName;
    if (draft.dob !== query.data.dob) changes.dob = draft.dob;
    if (draft.sexAtBirth !== (query.data.sex_at_birth ?? '')) changes.sexAtBirth = draft.sexAtBirth;
    if (draft.postalCode !== (query.data.postal_code ?? '')) changes.postalCode = draft.postalCode;
    if (draft.email !== (query.data.email ?? '')) changes.email = draft.email;
    if (draft.phone !== (query.data.phone ?? '')) changes.phone = draft.phone;
    if (draft.genderIdentity !== (query.data.gender_identity ?? ''))
      changes.genderIdentity = draft.genderIdentity;

    if (Object.keys(changes).length === 0) return;
    setPendingChanges(changes);
  }

  function handleConfirm(reason: string) {
    if (!pendingChanges) return;
    setError(null);
    updatePatient.mutate({ ...pendingChanges, reason } as UpdatePatientInput, {
      onSuccess: () => {
        setPendingChanges(null);
        setDirty(false);
      },
      onError: (err) => setError(parseMutationError(err, 'Could not update the patient.')),
    });
  }

  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <Title order={5}>Patient attributes</Title>

        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {error}
          </Alert>
        )}

        {query.isLoading || !draft ? (
          <Text c="dimmed">Loading patient…</Text>
        ) : query.isError ? (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            Could not load patient.
          </Alert>
        ) : (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <TextInput
                label="Given name"
                value={draft.givenName}
                readOnly={!canEdit}
                onChange={(e) => setField('givenName', e.currentTarget.value)}
              />
              <TextInput
                label="Family name"
                value={draft.familyName}
                readOnly={!canEdit}
                onChange={(e) => setField('familyName', e.currentTarget.value)}
              />
              <TextInput
                label="Date of birth"
                value={draft.dob}
                readOnly={!canEdit}
                onChange={(e) => setField('dob', e.currentTarget.value)}
              />
              <TextInput
                label="Sex at birth"
                value={draft.sexAtBirth}
                readOnly={!canEdit}
                onChange={(e) => setField('sexAtBirth', e.currentTarget.value)}
              />
              <TextInput
                label="Gender identity"
                value={draft.genderIdentity}
                readOnly={!canEdit}
                onChange={(e) => setField('genderIdentity', e.currentTarget.value)}
              />
              <TextInput
                label="Postal code"
                value={draft.postalCode}
                readOnly={!canEdit}
                onChange={(e) => setField('postalCode', e.currentTarget.value)}
              />
              <TextInput
                label="Email"
                value={draft.email}
                readOnly={!canEdit}
                onChange={(e) => setField('email', e.currentTarget.value)}
              />
              <TextInput
                label="Phone"
                value={draft.phone}
                readOnly={!canEdit}
                onChange={(e) => setField('phone', e.currentTarget.value)}
              />
            </SimpleGrid>
            {canEdit && (
              <Group justify="flex-end">
                <Button onClick={handleSave}>Save changes</Button>
              </Group>
            )}
          </>
        )}
      </Stack>

      <ReasonConfirmModal
        opened={pendingChanges !== null}
        title="Confirm action"
        description="This will correct patient PHI and is audit-logged."
        confirmLabel="Confirm"
        isPending={updatePatient.isPending}
        onConfirm={handleConfirm}
        onClose={() => {
          setPendingChanges(null);
          setError(null);
        }}
      />
    </Card>
  );
}
